// ===== v8 · SAC — Atendimento ao Cliente =====
import React from 'react';
import { Icon } from './icons.jsx';
import { useStore } from './store.jsx';
import { usePermission, usePermissions } from './auth.jsx';
import {
  PRODUTOS_BY_ID, SAC_CANAIS, SAC_CANAIS_BY_ID, SAC_PLAYBOOK, SAC_PRIORIDADES,
  SAC_PRIORIDADES_BY_ID, SAC_STATUS, SAC_STATUS_BY_ID, SAC_TEMPLATES, SAC_TIPOS, SAC_TIPOS_BY_ID,
  TODAY, brl, sacFmtHoras, sacSla, sacStatusParaTipo,
} from './data.jsx';
import { FilterBar } from './filterbar.jsx';
import { ModalShell, Select, Typeahead } from './modals.jsx';
import { SacFunil } from './sac-funil.jsx';

const { useState: useStateSac, useMemo: useMemoSac, useCallback: useCallbackSac, useRef: useRefSac, useEffect: useEffectSac } = React;

// Um negócio é "atendível pelo SAC" quando já existe pedido para reclamar:
// pedido emitido no Nomus, ou funil em Pedido/Produção/Faturamento/Ganho.
// (Antes só GANHO entrava — justamente "Status de Pedido" (SLA 4h) e
// "Divergência de Pedido", que ocorrem ANTES do ganho, ficavam sem vínculo.)
const SAC_STAGES_ATENDIVEIS = ["GANHO"];
function pedidoAtendivel(l) {
  return !l.perdido && (!!l.pedidoNomus || SAC_STAGES_ATENDIVEIS.includes(l.stage));
}

// ─── Chips / pills ───────────────────────────────────────────────────────────
function SacTipoChip({ tipoId, size }) {
  const t = SAC_TIPOS_BY_ID[tipoId];
  if (!t) return null;
  return (
    <span className="sac-tipo-chip" style={{ background: t.tone, color: t.color }}>
      <Icon name={t.icon} size={size === "sm" ? 10 : 11} /> {t.short}
    </span>
  );
}

function SacPriPill({ prioridade }) {
  const p = SAC_PRIORIDADES_BY_ID[prioridade] || SAC_PRIORIDADES_BY_ID.media;
  return (
    <span className={`sac-pri sac-pri-${prioridade}`}>
      <span className="d" /> {p.label}
    </span>
  );
}

function SacSlaPill({ chamado, withLabel }) {
  const sla = sacSla(chamado);
  if (sla.status === "resolvido" || sla.status === "fechado") {
    return <span className="sac-sla resolvido"><Icon name="check" size={11} /> Resolvido</span>;
  }
  if (sla.status === "pausado") {
    return (
      <span className="sac-sla pausado" title={`SLA pausado · ${sla.slaH}h tipo`}>
        <Icon name="pause-circle" size={11} /> {withLabel ? "SLA pausado" : "⏸"}
      </span>
    );
  }
  const map = {
    ok:        { cls: "ok",        icon: "clock",          txt: `${sacFmtHoras(sla.restanteH)} p/ SLA (${sla.slaH}h)` },
    risco:     { cls: "risco",     icon: "alert-triangle", txt: `${sacFmtHoras(sla.restanteH)} p/ SLA` },
    estourado: { cls: "estourado", icon: "alert-triangle", txt: `SLA +${sacFmtHoras(Math.abs(sla.restanteH))}` },
  };
  const m = map[sla.status] || map.ok;
  return (
    <span className={`sac-sla ${m.cls}`} title={`SLA ${sla.slaH}h por tipo`}>
      <Icon name={m.icon} size={11} /> {withLabel ? m.txt : sacFmtHoras(Math.abs(sla.restanteH))}
    </span>
  );
}

function SacCanalTag({ canal }) {
  const c = SAC_CANAIS_BY_ID[canal];
  if (!c) return null;
  return <span className="sac-canal-tag" title={`Canal: ${c.label}`}><Icon name={c.icon} size={11} /> {c.label}</span>;
}

// ─── Card ─────────────────────────────────────────────────────────────────────
function ChamadoCard({ chamado, onOpen, showAdvance }) {
  const store = useStore();
  const cliente = chamado.clienteId ? store.getClienteById(chamado.clienteId) : null;
  const resp = store.users.find(u => u.id === chamado.responsavelId);
  const sla = sacSla(chamado);
  const statusSeq = sacStatusParaTipo(chamado.tipo);
  const stIdx = statusSeq.findIndex(s => s.id === chamado.status);
  const nextStatus = statusSeq[stIdx + 1];
  const isEncerrado = chamado.status === "resolvido" || chamado.status === "fechado";

  return (
    <div className={`sac-card ${sla.status === "estourado" ? "alert" : ""}`}>
      <div className="sac-card-top" onClick={() => onOpen(chamado.id)}>
        <span className="sac-proto mono">{chamado.protocolo}</span>
        <SacPriPill prioridade={chamado.prioridade} />
      </div>
      <div className="sac-card-assunto" onClick={() => onOpen(chamado.id)}>{chamado.assunto}</div>
      <div className="sac-card-row">
        <SacTipoChip tipoId={chamado.tipo} />
        {!isEncerrado
          ? <SacSlaPill chamado={chamado} />
          : <span className="sac-csat-mini">{chamado.csat ? `★ ${chamado.csat}/5` : "CSAT —"}</span>}
      </div>
      {cliente && <div className="sac-card-cli"><Icon name="users" size={11} /> {cliente.nome}</div>}
      <div className="sac-card-meta">
        <span><span className="av xs">{resp ? resp.initials : "—"}</span> {resp ? resp.nome.split(" ")[0] : "—"}</span>
        <SacCanalTag canal={chamado.canal} />
      </div>
      {!isEncerrado && showAdvance && nextStatus && (
        <button className="sac-advance" onClick={e => { e.stopPropagation(); store.moveChamadoStatus(chamado.id, nextStatus.id); }}>
          {nextStatus.id === "resolvido" ? "Resolver" : `→ ${nextStatus.label}`} <Icon name="chevron-right" size={12} />
        </button>
      )}
    </div>
  );
}

// ─── Fila de trabalho (urgência) ──────────────────────────────────────────────
const FILA_GRUPOS = [
  { id: "estourado", label: "SLA Vencido",  icon: "alert-triangle", cls: "urg-vencido", desc: "Passaram do prazo" },
  { id: "risco",     label: "SLA em Risco", icon: "alert-circle",   cls: "urg-risco",   desc: "Acima de 75% do prazo" },
  { id: "ok",        label: "Em Dia",       icon: "check-circle",   cls: "urg-ok",      desc: "Dentro do prazo" },
  { id: "pausado",   label: "Aguardando",   icon: "pause-circle",   cls: "urg-pausado", desc: "SLA pausado · sem impacto" },
];

function FilaTrabalho({ chamados, onOpen }) {
  const grupos = useMemoSac(() => {
    const abertos = chamados.filter(c => c.status !== "resolvido" && c.status !== "fechado");
    const g = { estourado: [], risco: [], ok: [], pausado: [] };
    abertos.forEach(c => {
      const s = sacSla(c).status;
      (g[s] || g.ok).push(c);
    });
    Object.keys(g).forEach(k => g[k].sort((a, b) => sacSla(a).restanteH - sacSla(b).restanteH));
    return g;
  }, [chamados]);

  return (
    <div className="sac-board">
      {FILA_GRUPOS.map(g => {
        const items = grupos[g.id] || [];
        return (
          <div key={g.id} className={`pv-col sac-col sac-urg-col ${g.cls}`}>
            <div className="pv-col-head">
              <span className="pv-col-ic"><Icon name={g.icon} size={14} /></span>
              <div className="pv-col-tt">
                <div className="nm">{g.label}</div>
                <div className="ct mono">{items.length}</div>
              </div>
            </div>
            <div className="pv-col-desc">{g.desc}</div>
            <div className="pv-col-body">
              {items.length === 0 && <div className="pv-empty">—</div>}
              {items.map(c => <ChamadoCard key={c.id} chamado={c} onOpen={onOpen} showAdvance />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Kanban por status ────────────────────────────────────────────────────────
function KanbanStatus({ chamados, filtrados, onOpen }) {
  const porStatus = useMemoSac(() => {
    const m = {};
    SAC_STATUS.forEach(s => { m[s.id] = []; });
    filtrados.forEach(c => { (m[c.status] || m.aberto).push(c); });
    Object.keys(m).forEach(k => m[k].sort((a, b) => sacSla(a).restanteH - sacSla(b).restanteH));
    return m;
  }, [filtrados]);

  // Mostra só status com itens + padrão sempre visível
  const ALWAYS = new Set(["aberto", "em_atendimento", "aguardando_cliente", "resolvido"]);
  const visibles = SAC_STATUS.filter(s => s.id !== "fechado" && ((porStatus[s.id]?.length || 0) > 0 || ALWAYS.has(s.id)));

  return (
    <div className="sac-board">
      {visibles.map(st => {
        const items = porStatus[st.id] || [];
        return (
          <div key={st.id} className="pv-col sac-col">
            <div className="pv-col-head">
              <span className="pv-col-ic"><Icon name={st.icon} size={14} /></span>
              <div className="pv-col-tt">
                <div className="nm">{st.label}</div>
                <div className="ct mono">{items.length}</div>
              </div>
            </div>
            <div className="pv-col-desc">{st.desc}</div>
            <div className="pv-col-body">
              {items.length === 0 && <div className="pv-empty">—</div>}
              {items.map(c => <ChamadoCard key={c.id} chamado={c} onOpen={onOpen} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
// ─── Caixa única de filtros ────────────────────────────────────────────
// Um só controle: abre um painel com todos os grupos dentro. Os filtros ativos
// aparecem como chips ao lado, para não esconder o que está aplicado.
function SacFiltrosBox({ grupos, total, onClearAll }) {
  const [open, setOpen] = useStateSac(false);
  const ref = useRefSac(null);

  useEffectSac(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const k = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", h);
    document.addEventListener("keydown", k);
    return () => { document.removeEventListener("mousedown", h); document.removeEventListener("keydown", k); };
  }, []);

  return (
    <div className={`sfb${open ? " open" : ""}${total ? " active" : ""}`} ref={ref}>
      <button className="sfb-trigger" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <Icon name="filter" size={13} />
        <span>Filtros</span>
        {total > 0 && <span className="sfb-badge">{total}</span>}
        <Icon name="chevron-down" size={11} className="sfb-arrow" />
      </button>

      {open && (
        <div className="sfb-panel">
          <div className="sfb-head">
            <span className="tt">Filtrar chamados</span>
            {total > 0 && <button className="sfb-clear" onClick={onClearAll}><Icon name="x-circle" size={12} /> Limpar tudo</button>}
          </div>
          <div className="sfb-body">
            {grupos.map(g => (
              <div key={g.id} className="sfb-group">
                <div className="sfb-glabel">
                  {g.icon && <Icon name={g.icon} size={12} />}{g.label}
                  {g.value.length > 0 && <button className="sfb-gclear" onClick={() => g.onChange([])}>limpar</button>}
                </div>
                <div className="sfb-opts">
                  {g.options.map(opt => {
                    const on = g.value.includes(opt.value);
                    return (
                      <button key={opt.value} className={`sfb-opt${on ? " on" : ""}`}
                        onClick={() => g.onChange(on ? g.value.filter(v => v !== opt.value) : [...g.value, opt.value])}>
                        {opt.dot && <span className={`sac-status-dot st-${opt.value}`} />}
                        {opt.icon && <Icon name={opt.icon} size={12} style={{ color: on ? "inherit" : (opt.color || "inherit"), flexShrink: 0 }} />}
                        <span>{opt.label}</span>
                        {on && <Icon name="check" size={11} className="ck" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MultiFilterDropdown ───────────────────────────────────────────────
function MultiFilterDropdown({ label, icon, options, value, onChange }) {
  const [open, setOpen] = useStateSac(false);
  const ref = useRefSac(null);
  const active = value.length > 0;

  useEffectSac(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (id) => {
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
  };

  return (
    <div className={`mfd-wrap ${open ? "open" : ""} ${active ? "active" : ""}`} ref={ref}>
      <button className="mfd-trigger" onClick={() => setOpen(o => !o)}>
        {icon && <Icon name={icon} size={12} />}
        <span>{label}</span>
        {active && <span className="mfd-badge">{value.length}</span>}
        <Icon name="chevron-down" size={11} className="mfd-arrow" />
      </button>
      {open && (
        <div className="mfd-menu">
          {active && (
            <button className="mfd-clear" onClick={() => { onChange([]); setOpen(false); }}>
              <Icon name="x" size={11} /> Limpar seleção
            </button>
          )}
          {options.map(opt => {
            const checked = value.includes(opt.value);
            return (
              <button key={opt.value} className={`mfd-opt ${checked ? "checked" : ""}`} onClick={() => toggle(opt.value)}>
                <span className="mfd-chk">{checked && <Icon name="check" size={11} />}</span>
                {opt.icon && <Icon name={opt.icon} size={12} style={{ color: opt.color || "inherit", flexShrink: 0 }} />}
                {opt.dot && <span className={`sac-status-dot st-${opt.value}`} />}
                <span className="mfd-opt-label">{opt.label}</span>
                {opt.badge != null && <span className="mfd-opt-count">{opt.badge}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Banner de alertas de SLA ─────────────────────────────────────────────────
function SlaAlertBanner({ chamados }) {
  const vencidos = chamados.filter(c => {
    if (c.status === "resolvido" || c.status === "fechado") return false;
    return sacSla(c).status === "estourado";
  });
  const emRisco = chamados.filter(c => {
    if (c.status === "resolvido" || c.status === "fechado") return false;
    return sacSla(c).status === "risco";
  });
  const aguardando48h = chamados.filter(c => {
    if (c.status !== "aguardando_cliente") return false;
    return (TODAY - c.abertoEm) / 3600e3 >= 48;
  });

  if (!vencidos.length && !emRisco.length && !aguardando48h.length) return null;

  return (
    <div className="sac-alert-banner">
      {vencidos.length > 0 && (
        <div className="sab-item danger">
          <Icon name="alert-triangle" size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>{vencidos.length} SLA vencido{vencidos.length > 1 ? "s" : ""}</strong>
            {" — "}{vencidos.map(c => c.protocolo).join(", ")} · atendente e gestor notificados
          </span>
        </div>
      )}
      {emRisco.length > 0 && (
        <div className="sab-item warn">
          <Icon name="alert-circle" size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>{emRisco.length} em risco de SLA</strong> (&gt;75% consumido)
            {" — "}{emRisco.map(c => c.protocolo).join(", ")}
          </span>
        </div>
      )}
      {aguardando48h.length > 0 && (
        <div className="sab-item info">
          <Icon name="clock" size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>{aguardando48h.length} aguardando cliente há 48h+</strong>
            {" — "}{aguardando48h.map(c => c.protocolo).join(", ")} · fechar ou dar andamento?
          </span>
        </div>
      )}
    </div>
  );
}

function SacPage({ onOpenChamado, onNewChamado }) {
  const store = useStore();
  const permsSac = usePermissions('sac.create');
  const [view, setView] = useStateSac("funil");
  const [fTipos, setFTipos] = useStateSac([]);
  const [fStatus, setFStatus] = useStateSac([]);
  const [fPrio, setFPrio] = useStateSac([]);
  const [fCanal, setFCanal] = useStateSac([]);
  const [fResp, setFResp] = useStateSac([]);
  const [q, setQ] = useStateSac("");

  const hasFilter = fTipos.length || fStatus.length || fPrio.length || fCanal.length || fResp.length || q;
  const clearFilters = () => { setFTipos([]); setFStatus([]); setFPrio([]); setFCanal([]); setFResp([]); setQ(""); };

  const filtrados = useMemoSac(() => store.chamados.filter(c => {
    if (fTipos.length && !fTipos.includes(c.tipo)) return false;
    if (fStatus.length && !fStatus.includes(c.status)) return false;
    if (fPrio.length && !fPrio.includes(c.prioridade)) return false;
    if (fCanal.length && !fCanal.includes(c.canal)) return false;
    if (fResp.length && !fResp.includes(c.responsavelId)) return false;
    if (q) {
      const ql = q.toLowerCase();
      const cli = c.clienteId ? store.getClienteById(c.clienteId) : null;
      if (!c.assunto.toLowerCase().includes(ql) && !c.protocolo.toLowerCase().includes(ql) && !(cli?.nome || "").toLowerCase().includes(ql)) return false;
    }
    return true;
  }), [store.chamados, fTipos, fStatus, fPrio, fCanal, fResp, q]);

  const abertos = store.chamados.filter(c => c.status !== "resolvido" && c.status !== "fechado");
  const emRisco = abertos.filter(c => { const s = sacSla(c).status; return s === "risco" || s === "estourado"; }).length;
  const resolvidos = store.chamados.filter(c => c.status === "resolvido" || c.status === "fechado");
  const comCsat = resolvidos.filter(c => c.csat != null);
  const csatMedio = comCsat.length ? Math.round(comCsat.reduce((s, c) => s + c.csat, 0) / comCsat.length * 10) / 10 : 0;
  const tmrHoras = resolvidos.length
    ? Math.round(resolvidos.reduce((s, c) => s + (c.resolvidoEm ? (c.resolvidoEm - c.abertoEm) / 3600e3 : 0), 0) / resolvidos.length) : 0;
  const slaCumprido = resolvidos.filter(c => {
    const tipo = SAC_TIPOS_BY_ID[c.tipo];
    return c.resolvidoEm && tipo && (c.resolvidoEm - c.abertoEm) / 3600e3 <= tipo.slaH;
  }).length;
  const slaPct = resolvidos.length ? Math.round(slaCumprido / resolvidos.length * 100) : null;

  return (
    <div className="main main-scroll" data-screen-label="SAC">
      <FilterBar>
        <div className="seg">
          <button className={view === "funil" ? "on" : ""} onClick={() => setView("funil")}><Icon name="filter" size={13} /> Funil</button>
          <button className={view === "fila" ? "on" : ""} onClick={() => setView("fila")}><Icon name="layers" size={13} /> Fila</button>
          <button className={view === "kanban" ? "on" : ""} onClick={() => setView("kanban")}><Icon name="layout-kanban" size={13} /> Kanban</button>
          <button className={view === "table" ? "on" : ""} onClick={() => setView("table")}><Icon name="file-text" size={13} /> Tabela</button>
        </div>
        <div className="fb-search" style={{ maxWidth: 260 }}>
          <Icon name="search" size={13} style={{ color: "var(--color-ink-soft)" }} />
          <input placeholder="Protocolo, assunto ou cliente…" value={q} onChange={e => setQ(e.target.value)} />
          {q && <button className="icon-btn" style={{ width: 22, height: 22 }} onClick={() => setQ("")}><Icon name="x" size={12} /></button>}
        </div>

        <div className="sfb-slot" style={{ marginLeft: "auto" }}>
        <SacFiltrosBox
          total={fTipos.length + fStatus.length + fPrio.length + fCanal.length + fResp.length}
          onClearAll={clearFilters}
          grupos={[
            { id: "tipo", label: "Tipo", icon: "tag", value: fTipos, onChange: setFTipos,
              options: SAC_TIPOS.map(t => ({ value: t.id, label: t.label, icon: t.icon, color: t.color })) },
            { id: "status", label: "Status", icon: "activity", value: fStatus, onChange: setFStatus,
              options: SAC_STATUS.filter(s => s.id !== "fechado").map(s => ({ value: s.id, label: s.label, dot: true })) },
            { id: "prio", label: "Prioridade", icon: "alert-triangle", value: fPrio, onChange: setFPrio,
              options: SAC_PRIORIDADES.map(p => ({ value: p.id, label: p.label })) },
            { id: "canal", label: "Canal", icon: "radio", value: fCanal, onChange: setFCanal,
              options: SAC_CANAIS.map(c => ({ value: c.id, label: c.label, icon: c.icon })) },
            { id: "resp", label: "Responsável", icon: "user", value: fResp, onChange: setFResp,
              options: store.users.map(u => ({ value: u.id, label: u.nome })) },
          ]}
        />
        </div>
        {permsSac['sac.create'] && (
          <button className="btn primary" style={{ marginLeft: "auto" }} onClick={() => onNewChamado()}>
            <Icon name="plus" size={13} /> Novo chamado
          </button>
        )}
      </FilterBar>

      <div style={{ padding: 22, display: "flex", flexDirection: "column", gap: 18 }}>
        <SlaAlertBanner chamados={store.chamados} />
        {/* KPIs */}
        <div className="kpi-strip">
          <div className="kpi-stat tone-accent">
            <div className="kpi-ic"><Icon name="life-buoy" size={16} /></div>
            <div className="kpi-body"><div className="kpi-label">Chamados abertos</div><div className="kpi-valor mono">{abertos.length}</div><div className="kpi-sub">em atendimento</div></div>
          </div>
          <div className={`kpi-stat ${emRisco ? "tone-danger" : "tone-ok"}`}>
            <div className="kpi-ic"><Icon name="alert-triangle" size={16} /></div>
            <div className="kpi-body"><div className="kpi-label">SLA em risco</div><div className="kpi-valor mono">{emRisco}</div><div className="kpi-sub">no prazo limite ou estourado</div></div>
          </div>
          <div className={`kpi-stat ${slaPct === null ? "tone-accent" : slaPct >= 80 ? "tone-ok" : slaPct >= 60 ? "tone-warn" : "tone-danger"}`}>
            <div className="kpi-ic"><Icon name="check-circle-2" size={16} /></div>
            <div className="kpi-body">
              <div className="kpi-label">SLA cumprido</div>
              <div className="kpi-valor mono">{slaPct !== null ? `${slaPct}%` : "—"}</div>
              <div className="kpi-sub">TMR {sacFmtHoras(tmrHoras)}</div>
            </div>
          </div>
          <div className={`kpi-stat ${csatMedio >= 4 ? "tone-ok" : csatMedio > 0 ? "tone-warn" : "tone-accent"}`}>
            <div className="kpi-ic"><Icon name="smile" size={16} /></div>
            <div className="kpi-body"><div className="kpi-label">CSAT médio</div><div className="kpi-valor mono">{csatMedio || "—"}<span style={{ fontSize: 13, color: "var(--color-ink-faint)" }}>{csatMedio ? "/5" : ""}</span></div><div className="kpi-sub">{comCsat.length} avaliações</div></div>
          </div>
        </div>

        {/* SLA info */}
        <div className="pv-rule-note">
          <Icon name="life-buoy" size={14} />
          <span>
            <strong>SLA por tipo:</strong>{" "}
            {SAC_TIPOS.map((t, i) => (
              <span key={t.id}>{i > 0 && " · "}<span style={{ color: t.color, fontWeight: 700 }}>{t.short}</span> {t.slaH}h</span>
            ))}.{" "}
            SLA <strong>pausa</strong> quando aguardando cliente ou retorno do produto.
          </span>
        </div>

        {/* Views */}
        {view === "funil" && <SacFunil chamados={filtrados} onOpen={onOpenChamado} />}
        {view === "fila" && <FilaTrabalho chamados={filtrados} onOpen={onOpenChamado} />}
        {view === "kanban" && <KanbanStatus chamados={store.chamados} filtrados={filtrados} onOpen={onOpenChamado} />}
        {view === "table" && (
          <div className="card-section">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Protocolo</th>
                  <th>Assunto</th>
                  <th>Tipo</th>
                  <th>Cliente</th>
                  <th>Canal</th>
                  <th>Prioridade</th>
                  <th>Status</th>
                  <th>Responsável</th>
                  <th>SLA</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.slice().sort((a, b) => sacSla(a).restanteH - sacSla(b).restanteH).map(c => {
                  const cli = c.clienteId ? store.getClienteById(c.clienteId) : null;
                  const resp = store.users.find(u => u.id === c.responsavelId);
                  const st = SAC_STATUS_BY_ID[c.status];
                  return (
                    <tr key={c.id} style={{ cursor: "pointer" }} onClick={() => onOpenChamado(c.id)}>
                      <td className="mono" style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{c.protocolo}</td>
                      <td style={{ fontWeight: 600, maxWidth: 260 }}>{c.assunto}</td>
                      <td><SacTipoChip tipoId={c.tipo} /></td>
                      <td style={{ fontSize: 12 }}>{cli ? cli.nome : "—"}</td>
                      <td><SacCanalTag canal={c.canal} /></td>
                      <td><SacPriPill prioridade={c.prioridade} /></td>
                      <td><span className="sac-status-tag"><Icon name={st?.icon} size={11} /> {st?.label}</span></td>
                      <td style={{ fontSize: 12 }}>{resp ? resp.nome : "—"}</td>
                      <td><SacSlaPill chamado={c} withLabel /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Stars CSAT ───────────────────────────────────────────────────────────────
function CsatStars({ value, onChange, readOnly }) {
  const [hover, setHover] = useStateSac(0);
  return (
    <div className="csat-stars">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" className={`csat-star ${(hover || value) >= n ? "on" : ""}`} disabled={readOnly}
          onMouseEnter={() => !readOnly && setHover(n)} onMouseLeave={() => !readOnly && setHover(0)}
          onClick={() => !readOnly && onChange(n)}>
          <Icon name="star" size={readOnly ? 14 : 22} />
        </button>
      ))}
    </div>
  );
}

// ─── Copy helper ──────────────────────────────────────────────────────────────
function useCopy() {
  const [copied, setCopied] = useStateSac(null);
  const copy = useCallbackSac((text, key) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  }, []);
  return { copy, copied };
}

// ─── Drawer de detalhe ────────────────────────────────────────────────────────
function SacDrawer({ chamadoId, onClose, onOpenCliente, onOpenLead }) {
  const store = useStore();
  const permsDrawer = usePermissions('sac.create', 'sac.resolve');
  const podeAtuar = permsDrawer['sac.create'];
  const podeResolver = permsDrawer['sac.resolve'];
  const chamado = store.chamados.find(c => c.id === chamadoId);
  const [nota, setNota] = useStateSac("");
  const [resolving, setResolving] = useStateSac(false);
  const [csat, setCsat] = useStateSac(5);
  const [resol, setResol] = useStateSac("");
  const { copy, copied } = useCopy();

  if (!chamado) return null;

  const tipo = SAC_TIPOS_BY_ID[chamado.tipo];
  const cliente = chamado.clienteId ? store.getClienteById(chamado.clienteId) : null;
  const produto = chamado.produtoId ? PRODUTOS_BY_ID[chamado.produtoId] : null;
  const lead = chamado.leadId ? store.leads.find(l => l.id === chamado.leadId) : null;
  const resp = store.users.find(u => u.id === chamado.responsavelId);
  const st = SAC_STATUS_BY_ID[chamado.status];
  const statusSeq = sacStatusParaTipo(chamado.tipo);
  const stIdx = statusSeq.findIndex(s => s.id === chamado.status);
  const sla = sacSla(chamado);
  const isEncerrado = chamado.status === "resolvido" || chamado.status === "fechado";
  const playbook = SAC_PLAYBOOK[chamado.tipo] || { passos: [], template: "" };
  const passosDone = chamado.passosDone || [];
  const passosCompletos = playbook.passos.length === 0 || playbook.passos.every((_, i) => passosDone.includes(i));
  const pedidosCliente = chamado.clienteId ? store.getLeadsByCliente(chamado.clienteId).filter(pedidoAtendivel) : [];

  const voltarEtapa = () => { if (podeAtuar && stIdx > 0) store.moveChamadoStatus(chamado.id, statusSeq[stIdx - 1].id); };
  const avancarEtapa = () => {
    const nx = statusSeq[stIdx + 1];
    if (!nx) return;
    if (!podeAtuar) return;
    if (nx.id === "resolvido") { if (!podeResolver) return; if (!resol.trim()) setResol(playbook.template || ""); setResolving(true); }
    else store.moveChamadoStatus(chamado.id, nx.id);
  };
  const adicionarNota = () => {
    const t = nota.trim(); if (!t) return;
    store.addChamadoInteracao(chamado.id, { texto: t, autorTipo: "humano", canal: "nota" });
    setNota("");
  };
  const confirmarResolucao = () => {
    store.resolveChamado(chamado.id, { resolucao: resol.trim() || undefined, csat });
    setResolving(false); setResol("");
  };

  // Avisos contextuais por status
  const avisoStatus = (() => {
    if (chamado.status === "aguardando_retorno") return {
      msg: "Lembre-se de comunicar ao cliente as instruções de retorno do produto.",
      tpl: SAC_TEMPLATES.aguardando_retorno(chamado.protocolo),
      key: "retorno",
    };
    if (chamado.status === "produto_recebido") return {
      msg: "Informe ao cliente que o produto foi recebido e está em análise.",
      tpl: SAC_TEMPLATES.produto_recebido(chamado.protocolo),
      key: "recebido",
    };
    if (chamado.status === "resolvido") return {
      msg: "Envie a pesquisa de satisfação ao cliente.",
      tpl: SAC_TEMPLATES.survey(chamado.protocolo),
      key: "survey",
    };
    return null;
  })();

  return (
    <aside className="drawer sac-drawer" data-screen-label={`SAC / ${chamado.protocolo}`}>
      <div className="drawer-head">
        <div className="top">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="drawer-eyebrow">
              <span className="sac-eyebrow-tipo" style={{ color: tipo?.color }}><Icon name={tipo?.icon} size={11} /> {tipo?.label}</span>
              <span className="sep">·</span>
              <span className="mono">{chamado.protocolo}</span>
            </div>
            <div className="title">{chamado.assunto}</div>
            <div className="meta">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span className={`sac-status-dot st-${chamado.status}`} /> {st?.label}
              </span>
              <span>·</span>
              <SacPriPill prioridade={chamado.prioridade} />
              <span>·</span>
              <span>aberto {chamado.abertoQuando}</span>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Fechar"><Icon name="x" size={16} /></button>
        </div>

        {/* SLA banner */}
        <div className={`sac-sla-banner ${sla.status}`}>
          <Icon name={isEncerrado ? "check-circle-2" : sla.status === "pausado" ? "pause-circle" : sla.status === "ok" ? "clock" : "alert-triangle"} size={15} />
          <div style={{ flex: 1 }}>
            {isEncerrado
              ? <span><strong>Resolvido</strong>{chamado.resolvidoEm ? <> · em {sacFmtHoras((chamado.resolvidoEm - chamado.abertoEm) / 3600e3)}</> : null}</span>
              : sla.status === "pausado"
                ? <span><strong>SLA pausado</strong> · aguardando · {sacFmtHoras(sla.restanteH)} restantes quando retomar</span>
                : sla.status === "estourado"
                  ? <span><strong>SLA estourado</strong> há {sacFmtHoras(Math.abs(sla.restanteH))} · prazo: {sla.slaH}h por tipo</span>
                  : <span><strong>{sacFmtHoras(sla.restanteH)}</strong> até o limite de SLA · {sla.slaH}h por tipo</span>
            }
          </div>
        </div>

        {/* Aviso contextual de status */}
        {avisoStatus && (
          <div className="sac-ctx-aviso">
            <Icon name="info" size={13} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1 }}>
              <span>{avisoStatus.msg}</span>
              <button className="sac-copy-btn" onClick={() => copy(avisoStatus.tpl, avisoStatus.key)}>
                <Icon name={copied === avisoStatus.key ? "check" : "copy"} size={11} />
                {avisoStatus.key === "survey" ? "Copiar link da pesquisa" : "Copiar mensagem"}
              </button>
            </div>
          </div>
        )}

        {/* Mensagem de abertura */}
        {!isEncerrado && (() => {
          const tplFn = SAC_TEMPLATES[`abertura_${chamado.tipo.replace("divergencia_pedido", "divergencia")}`]
            || SAC_TEMPLATES["abertura_suporte"];
          const nomeCliente = cliente?.nome || "cliente";
          const tpl = tplFn(nomeCliente, chamado.protocolo);
          return (
            <div className="sac-ctx-aviso" style={{ marginTop: 4 }}>
              <Icon name="send" size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ flex: 1 }}>
                <span>Mensagem de abertura WA pronta para envio.</span>
                <button className="sac-copy-btn" onClick={() => copy(tpl, "abertura")}>
                  <Icon name={copied === "abertura" ? "check" : "copy"} size={11} />
                  {copied === "abertura" ? "Copiado!" : "Copiar mensagem"}
                </button>
              </div>
            </div>
          );
        })()}

        {/* Vínculos */}
        <div className="sac-links">
          {cliente && <button className="sac-link" onClick={() => onOpenCliente(cliente.id)}><Icon name="users" size={12} /> {cliente.nome} <Icon name="external-link" size={11} style={{ color: "var(--color-ink-faint)" }} /></button>}
          {produto && <span className="sac-link static"><Icon name="package" size={12} /> {produto.nome}</span>}
          {chamado.pedidoNomus && <span className="sac-link static mono" title="Pedido Nomus vinculado"><Icon name="hash" size={11} /> {chamado.pedidoNomus}</span>}
          <span className="sac-link static"><span className="av xs">{resp ? resp.initials : "—"}</span> {resp ? resp.nome : "—"}</span>
          <span className="sac-link static"><Icon name={SAC_CANAIS_BY_ID[chamado.canal]?.icon} size={12} /> {SAC_CANAIS_BY_ID[chamado.canal]?.label}</span>
        </div>

        {/* Mover entre etapas */}
        {!isEncerrado && podeAtuar && (
          <div className="sac-move">
            <button className="sac-move-btn back" disabled={stIdx === 0} onClick={voltarEtapa} title="Voltar etapa">
              <Icon name="chevron-left" size={15} /> Voltar
            </button>
            <div className="sac-move-mid">
              <div className="sac-move-dots">
                {statusSeq.filter(s => s.id !== "fechado").map((s, i) => (
                  <button key={s.id}
                    className={`sac-move-dot ${i === stIdx ? "current" : ""} ${i < stIdx ? "done" : ""}`}
                    title={s.label}
                    onClick={() => s.id === "resolvido" ? (podeResolver && setResolving(true)) : store.moveChamadoStatus(chamado.id, s.id)}>
                    <Icon name={s.icon} size={12} />
                  </button>
                ))}
              </div>
              <div className="sac-move-now">{st?.label}</div>
            </div>
            <button className="sac-move-btn fwd" onClick={avancarEtapa} title="Avançar etapa"
              disabled={statusSeq[stIdx + 1]?.id === "resolvido" && !podeResolver}>
              {statusSeq[stIdx + 1]?.id === "resolvido" ? "Resolver" : "Avançar"} <Icon name="chevron-right" size={15} />
            </button>
          </div>
        )}
      </div>

      <div className="drawer-body sac-drawer-body">
        {chamado.descricao && (
          <div className="sac-desc">
            <div className="sac-sec-h"><Icon name="file-text" size={12} /> Descrição da ocorrência</div>
            <p>{chamado.descricao}</p>
          </div>
        )}

        {playbook.passos.length > 0 && (
          <div className="sac-playbook">
            <div className="sac-sec-h">
              <Icon name="list" size={12} /> Checklist de atendimento
              <span className="sac-play-prog">{passosDone.length}/{playbook.passos.length}</span>
              <div className="sac-play-bar"><div className="sac-play-fill" style={{ width: `${Math.round(passosDone.length / playbook.passos.length * 100)}%` }} /></div>
            </div>
            <div className="sac-play-steps">
              {playbook.passos.map((p, i) => {
                const done = passosDone.includes(i);
                return (
                  <button key={i} className={`sac-play-step ${done ? "done" : ""}`} disabled={isEncerrado}
                    onClick={() => !isEncerrado && store.togglePassoChamado(chamado.id, i)}>
                    <span className="sac-play-check"><Icon name={done ? "check" : "circle"} size={12} /></span>
                    <span className="sac-play-txt">{p}</span>
                  </button>
                );
              })}
            </div>
            {!isEncerrado && passosCompletos && (
              <div className="sac-play-ready"><Icon name="check-circle-2" size={12} /> Checklist completo — pronto para resolver!</div>
            )}
          </div>
        )}

        {isEncerrado && (
          <div className="sac-resol-box">
            <div className="sac-sec-h"><Icon name="check-circle-2" size={12} /> Resolução</div>
            <p>{chamado.resolucao}</p>
            <div className="sac-csat-final">
              <span>CSAT</span>
              {chamado.csat != null ? <CsatStars value={chamado.csat} readOnly /> : <em style={{ color: "var(--color-ink-faint)" }}>não avaliado</em>}
            </div>
            {podeResolver && <button className="btn sm" onClick={() => store.reopenChamado(chamado.id)}><Icon name="rotate-ccw" size={12} /> Reabrir chamado</button>}
          </div>
        )}

        {resolving && !isEncerrado && podeResolver && (
          <div className="sac-resolve-panel">
            <div className="sac-sec-h"><Icon name="check-circle-2" size={12} /> Concluir atendimento</div>
            <label className="sac-field-lb">Resumo da resolução {playbook.template && !resol.trim() && <span className="sac-play-tpl-hint" onClick={() => setResol(playbook.template)}>usar template</span>}</label>
            <textarea className="sac-textarea" rows={3} value={resol} onChange={e => setResol(e.target.value)} placeholder={playbook.template || "Descreva como o chamado foi resolvido."} />
            <label className="sac-field-lb">CSAT — satisfação do cliente</label>
            <CsatStars value={csat} onChange={setCsat} />
            <div className="sac-ctx-aviso" style={{ marginTop: 10 }}>
              <Icon name="send" size={12} />
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 12 }}>Envie a pesquisa de satisfação ao cliente após resolver.</span>
                <button className="sac-copy-btn" onClick={() => copy(SAC_TEMPLATES.survey(chamado.protocolo), "survey_resolve")}>
                  <Icon name={copied === "survey_resolve" ? "check" : "copy"} size={11} /> Copiar link da pesquisa
                </button>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button className="btn primary sm" onClick={confirmarResolucao}><Icon name="check" size={12} /> Resolver chamado</button>
              <button className="btn ghost sm" onClick={() => setResolving(false)}>Cancelar</button>
            </div>
          </div>
        )}

        {/* Pedido */}
        <div className="sac-pedido">
          <div className="sac-sec-h"><Icon name="package" size={12} /> Pedido vinculado</div>
          {lead ? (
            <div className="sac-pedido-row">
              <button className="sac-pedido-card" onClick={() => onOpenLead(lead.id)}>
                <Icon name="layout-kanban" size={13} />
                <span className="nm">{lead.empresa}</span>
                <span className="vl mono">{brl(lead.valor)}</span>
                <Icon name="external-link" size={12} style={{ color: "var(--color-ink-faint)" }} />
              </button>
              {!isEncerrado && <button className="sac-pedido-unlink" onClick={() => store.updateChamado(chamado.id, { leadId: null })}><Icon name="x" size={13} /></button>}
            </div>
          ) : pedidosCliente.length ? (
            <Select value="" onChange={v => v && store.updateChamado(chamado.id, { leadId: v })}
              options={[{ value: "", label: "Vincular a um pedido…" }, ...pedidosCliente.map(l => ({ value: l.id, label: `${l.empresa} · ${brl(l.valor)}` }))]} />
          ) : (
            <div className="sac-pedido-empty">Nenhum pedido fechado para este cliente.</div>
          )}
        </div>

        {/* Andamento */}
        <div className="sac-sec-h" style={{ marginTop: 4 }}>
          <Icon name="history" size={12} /> Andamento <span className="mono" style={{ color: "var(--color-ink-faint)" }}>{chamado.interacoes.length}</span>
        </div>
        <div className="sac-andamento">
          {chamado.interacoes.length === 0 && <div className="sac-pedido-empty">Sem registros ainda.</div>}
          {chamado.interacoes.map(it => {
            const isSys = it.canal === "sistema";
            const isNota = it.canal === "nota";
            const icon = isSys ? "git-merge" : isNota ? "file-text" : (SAC_CANAIS_BY_ID[it.canal]?.icon || "message-circle");
            return (
              <div key={it.id} className={`sac-ev ${isSys ? "sys" : ""} ${isNota ? "nota" : ""}`}>
                <div className="sac-ev-dot"><Icon name={icon} size={11} /></div>
                <div className="sac-ev-body">
                  <div className="sac-ev-head">
                    <span className="who">{isSys ? "Sistema" : it.autorTipo === "cliente" ? "Cliente" : it.autor}</span>
                    {!isSys && !isNota && <span className="tag"><Icon name={SAC_CANAIS_BY_ID[it.canal]?.icon} size={9} /> {SAC_CANAIS_BY_ID[it.canal]?.label}</span>}
                    {isNota && <span className="tag nota">Observação interna</span>}
                    <span className="t">{it.when}</span>
                  </div>
                  <div className="sac-ev-txt">{it.texto}</div>
                </div>
              </div>
            );
          })}
        </div>

        {!isEncerrado && (
          <div className="sac-note-add">
            <textarea className="sac-textarea" rows={2} value={nota} onChange={e => setNota(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) adicionarNota(); }}
              placeholder="Registrar observação interna (apuração, contato, decisão)…" />
            <div className="sac-composer-foot">
              <span className="hint"><Icon name="info" size={11} /> Visível só para a equipe</span>
              <button className="btn primary sm" onClick={adicionarNota} disabled={!nota.trim()}><Icon name="plus" size={12} /> Adicionar</button>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

// ─── Modal · Novo chamado ─────────────────────────────────────────────────────
function NewChamadoModal({ onClose, clienteId, leadId }) {
  const store = useStore();
  const podeCriarChamado = usePermission('sac.create');
  const [form, setForm] = useStateSac({
    clienteId: clienteId || "",
    leadId: leadId || "",
    produtoId: "",
    tipo: "suporte",
    canal: "whatsapp",
    prioridade: "media",
    responsavelId: store.me.id,
    assunto: "",
    descricao: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const clienteOpts = store.clientes.filter(c => !c.isCanal).map(c => ({ value: c.id, label: c.nome, sub: c.cidade, icon: "users" }));
  const pedidosCliente = form.clienteId ? store.getLeadsByCliente(form.clienteId).filter(pedidoAtendivel) : [];

  const onPickCliente = id => {
    const cli = store.getClienteById(id);
    const pedidos = store.getLeadsByCliente(id).filter(pedidoAtendivel);
    setForm(f => ({ ...f, clienteId: id, leadId: pedidos.length === 1 ? pedidos[0].id : "", responsavelId: cli?.ownerId || f.responsavelId }));
  };

  const tipoSel = SAC_TIPOS_BY_ID[form.tipo];

  const submit = () => {
    if (!podeCriarChamado) { store.toast({ kind: "err", msg: "Seu login não pode abrir chamados" }); return; }
    if (!form.clienteId) { store.toast({ kind: "err", msg: "Selecione o cliente do chamado" }); return; }
    if (!form.assunto.trim()) { store.toast({ kind: "err", msg: "Descreva o assunto do chamado" }); return; }
    store.createChamado({ ...form, clienteId: form.clienteId, leadId: form.leadId || null, produtoId: form.produtoId || null });
    onClose();
  };

  return (
    <ModalShell title="Novo chamado de SAC" icon="life-buoy" onClose={onClose} width={640} footer={
      <>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        {podeCriarChamado && <button className="btn primary" onClick={submit}><Icon name="life-buoy" size={13} /> Abrir chamado</button>}
      </>
    }>
      <div className="field">
        <label>Cliente <span className="req">*</span></label>
        <Typeahead value={form.clienteId} onChange={v => v ? onPickCliente(v) : set("clienteId", "")} options={clienteOpts} placeholder="Buscar cliente da carteira…" icon="users" />
      </div>

      <div className="field">
        <label>Pedido vinculado <span className="opt">(opcional)</span></label>
        {!form.clienteId
          ? <div className="sac-pedido-empty">Selecione um cliente para listar os pedidos.</div>
          : pedidosCliente.length > 0
            ? <Select value={form.leadId} onChange={v => set("leadId", v)} options={[{ value: "", label: "Sem vínculo de pedido" }, ...pedidosCliente.map(l => ({ value: l.id, label: `${l.empresa}${l.pedidoNomus ? " · " + l.pedidoNomus : ""} · ${brl(l.valor)}` }))]} />
            : <div className="sac-pedido-empty">Nenhum pedido fechado para este cliente.</div>}
      </div>

      {(form.tipo === "divergencia_pedido" || form.tipo === "status_pedido") && (
        <div className="field">
          <label>Nº do Pedido Nomus (CP) <span className="opt">(opcional mas recomendado)</span></label>
          <div className="control mono">
            <Icon name="hash" size={13} style={{ color: "var(--color-ink-soft)", marginRight: 8 }} />
            <input placeholder="CP 09969" value={form.pedidoNomus || ""} onChange={e => set("pedidoNomus", e.target.value)} />
          </div>
          {tipoSel?.temLaude && (
            <div className="sac-tipo-hint"><Icon name="alert-circle" size={12} /> Para divergência de pedido, informe o CP para agilizar a apuração.</div>
          )}
        </div>
      )}

      <div className="field">
        <label>Tipo de ocorrência</label>
        <div className="sac-tipo-pick">
          {SAC_TIPOS.map(t => (
            <button key={t.id} type="button"
              className={`sac-tipo-opt ${form.tipo === t.id ? "on" : ""}`}
              style={form.tipo === t.id ? { borderColor: t.color, background: t.tone, color: t.color } : {}}
              onClick={() => set("tipo", t.id)}>
              <Icon name={t.icon} size={13} />
              <div>
                <div style={{ fontWeight: 700 }}>{t.label}</div>
                <div style={{ fontSize: 11, opacity: 0.75 }}>SLA {t.slaH}h</div>
              </div>
            </button>
          ))}
        </div>
        {tipoSel?.temLaude && (
          <div className="sac-tipo-hint"><Icon name="file-text" size={12} /> Este tipo pode exigir laude técnico e retorno do produto.</div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div className="field">
          <label>Canal de entrada</label>
          <Select value={form.canal} onChange={v => set("canal", v)} options={SAC_CANAIS.map(c => ({ value: c.id, label: c.label }))} />
        </div>
        <div className="field">
          <label>Prioridade</label>
          <Select value={form.prioridade} onChange={v => set("prioridade", v)} options={SAC_PRIORIDADES.map(p => ({ value: p.id, label: p.label }))} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div className="field">
          <label>Responsável</label>
          <Select value={form.responsavelId} onChange={v => set("responsavelId", v)} options={store.users.map(u => ({ value: u.id, label: u.nome }))} />
        </div>
      </div>

      <div className="field">
        <label>Assunto <span className="req">*</span></label>
        <div className="control"><input value={form.assunto} onChange={e => set("assunto", e.target.value)} placeholder={`Ex.: ${tipoSel?.short} — descreva em uma linha`} /></div>
      </div>

      <div className="field">
        <label>Relato do cliente <span className="opt">(vira a 1ª mensagem)</span></label>
        <textarea className="sac-textarea" rows={3} value={form.descricao} onChange={e => set("descricao", e.target.value)} placeholder="O que o cliente relatou, com o máximo de detalhes." />
      </div>
    </ModalShell>
  );
}

export { SacPage, ChamadoCard, SacDrawer, NewChamadoModal, CsatStars, SacPriPill };
